import { useEffect, useState } from 'react';
import { companiesAPI } from '../services/companies';
import { strengthUnitFromFormat } from './strengthUnit';

/**
 * A company's strength unit (MPa / Psi / N/mm²) from its StrengthFormat
 * setting. Null until it has loaded, when there is no company ID, or when
 * the setting cannot be read: callers then show the value without a unit
 * rather than guess one.
 */
export const useCompanyStrengthUnit = (companyId?: string | null): string | null => {
  // The unit is kept with the company it was read for, so when the company
  // changes the previous company's unit is never shown while the new one
  // loads.
  const [loaded, setLoaded] = useState<{ companyId: string; unit: string | null } | null>(null);

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    companiesAPI
      .getCompanyById(companyId)
      .then((company) => {
        if (!cancelled) setLoaded({ companyId, unit: strengthUnitFromFormat(company?.strengthformat) });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ companyId, unit: null });
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return companyId && loaded?.companyId === companyId ? loaded.unit : null;
};

/** Whether two company IDs name the same company (GUIDs, any case). */
export const sameCompany = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Strength units for two companies, such as a record's company and the
 * signed-in user's (whose company a new mix design belongs to). Reads the
 * company once when both IDs name the same company. Each is null until known.
 */
export const useCompanyStrengthUnits = (
  firstId?: string | null,
  secondId?: string | null,
): [string | null, string | null] => {
  const same = sameCompany(firstId, secondId);
  const first = useCompanyStrengthUnit(firstId);
  const second = useCompanyStrengthUnit(same ? null : secondId);
  return [first, same ? first : second];
};

const companyKey = (id: string): string => id.trim().toLowerCase();

/**
 * Strength units for a list of companies (for example the mix designs on a
 * project, which can belong to different companies), each company read
 * once. Returns a lookup that gives a company's unit, or null while it loads,
 * when it cannot be read, or for no company.
 */
export const useCompanyStrengthUnitLookup = (
  companyIds: (string | null | undefined)[],
): ((companyId?: string | null) => string | null) => {
  // One ID per company (any case), in a stable order, as the effect key.
  const firstSpelling = new Map<string, string>();
  companyIds.forEach((id) => {
    if (id && id.trim() && !firstSpelling.has(companyKey(id))) firstSpelling.set(companyKey(id), id.trim());
  });
  const idsKey = Array.from(firstSpelling.values()).sort().join('\n');
  const [units, setUnits] = useState<Record<string, string | null>>({});

  useEffect(() => {
    const ids = idsKey ? idsKey.split('\n') : [];
    let cancelled = false;
    ids.forEach((id) => {
      companiesAPI
        .getCompanyById(id)
        .then((company) => {
          if (!cancelled) {
            setUnits((prev) => ({ ...prev, [companyKey(id)]: strengthUnitFromFormat(company?.strengthformat) }));
          }
        })
        .catch(() => {
          if (!cancelled) setUnits((prev) => ({ ...prev, [companyKey(id)]: null }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return (companyId) => (companyId ? units[companyKey(companyId)] ?? null : null);
};
